import React, { useContext } from "react";
import { StyleSheet, ScrollView, TouchableOpacity, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { SessionContext } from "../../../../contexts";
import { NormalText, Tab, TitleText } from "../../../common/native";
import { TTabName } from "../../../../utils/types";
import ProfileTab from "./ProfileTab";
import { TAppearance } from "./AppearanceTab";
import AppearanceTab from "./AppearanceTab";
import DeployTab from "./DeployTab";
import DeployHelpWidget from "./DeployHelpWidget";
import { TProfile } from "./ProfileTab";
import { TFormFormatConfig, buildFormPreviewUrl } from "./FormFormatTab";
import { useOnboardingSettings } from "../../../../contexts/OnboardingSettingsContext";
import { useOnboardingTips } from "../../../../contexts/OnboardingTipsContext";
import { TOOLTIP_STEPS } from "../../../../utils/constant";
import { mixpanel } from "../../../../utils/mixpanel";
import DeployWidgetOnboardingCard from "./DeployWidgetOnboardingCard";
import LeadCaptureShareLinkSection from "./LeadCaptureShareLinkSection";
import {
  DEPLOY_WIDGET_ONBOARDING_STEPS,
  FIRST_DEPLOY_WIDGET_ONBOARDING_STEP_BY_TAB,
  TDeployWidgetOnboardingTab,
} from "./deployWidgetOnboardingContent";
import { ADMIN_TOKENS } from "../../../../styles/adminTokens";

const DEFAULT_WIDGET_TEXT_CONTENT = `Considering plastic surgery?
Visualize your next procedure
with our AI.`;
const DEFAULT_LAUNCHER_TEXT = "Visualize with AI";

type LeadConversionWorkflow = "social" | "website";

const LEAD_CONVERSION_CHANNEL_LABEL_BY_WORKFLOW: Record<
  LeadConversionWorkflow,
  string
> = {
  social: "social media",
  website: "website visitors",
};

const LEAD_CONVERSION_WORKFLOW_OPTIONS: {
  id: LeadConversionWorkflow;
  iconName: string;
  title: string;
  description: string;
}[] = [
  {
    id: "social",
    iconName: "share",
    title: "Social media",
    description:
      "Convert leads on your social media platforms using a standalone link. Use it on bios, posts, ads, or DMs.",
  },
  {
    id: "website",
    iconName: "language",
    title: "Website visitors",
    description:
      "Convert website visitors into leads with contact info and photos, using a custom website widget.",
  },
];

const buildIframeSnippet = (url: string) => `<iframe
  src="${url}"
  width="100%"
  height="900"
  style="border:0;"
  loading="lazy"
  allowfullscreen
></iframe>`;

const GetStarted: React.FC = () => {
  const session = useContext(SessionContext)?.session;
  const {
    hasSeenDeployWidgetOnboarding,
    markDeployWidgetOnboardingSeen,
    tourActive,
    showDeployWidgetTips,
  } = useOnboardingSettings();
  const { markStepComplete } = useOnboardingTips();
  const isTourDrivenOnboarding = tourActive && showDeployWidgetTips;

  // console.log("session", session);

  const [selectedWorkflow, setSelectedWorkflow] =
    React.useState<LeadConversionWorkflow | null>(null);
  const [selectedTab, setSelectedTab] = React.useState<TTabName>("Profile");
  const [visitedTabs, setVisitedTabs] = React.useState<Set<TTabName>>(
    () => new Set<TTabName>(["Profile"]),
  );
  const [profile, setProfile] = React.useState<TProfile>(() => {
    return {
      email: "",
      logoUrl: session?.logoUrl || "",
      logoUrlRectangle: session?.logoUrlRectangle || "",
    };
  });
  const [isFetchingWidgetSettings, setIsFetchingWidgetSettings] =
    React.useState<boolean>(false);
  const [areWidgetSettingsReady, setAreWidgetSettingsReady] =
    React.useState<boolean>(false);
  const hasFetchedWidgetSettingsRef = React.useRef(false);
  const [formFormat, setFormFormat] = React.useState<TFormFormatConfig>({
    formName: "onboarding-tier1",
    lang: "en",
    procedure: "BBL",
    configUrl: "",
    name: "",
    email: "",
    phone: "",
    referrer: "",
    formVersion: "",
    isMobile: "false",
    ctaCopy: "",
    collectEmail: "true",
    collectSmsConsent: "true",
  });
  const isAdminMode = React.useMemo(() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    return params.has("admin");
  }, []);

  const [appearance, setAppearance] = React.useState<TAppearance>({
    launcherStyle: "classic",
    launcherText: DEFAULT_LAUNCHER_TEXT,
    textContent: DEFAULT_WIDGET_TEXT_CONTENT,
    fontFamily: "Urbanist",
    ctaPosition: "above",
    ctaPositionDesktop: "right",
    ctaPositionMobile: "right",
    horizontalOffsetDesktop: "20",
    horizontalOffsetMobile: "20",
    isAutoPlayDesktop: "true",
    isAutoPlayMobile: "true",
    isCtaOpenByDefaultDesktop: "true",
    isCtaOpenByDefaultMobile: "true",
    textColor: "#ffffff",
    themeColor: "#00b7f4",
    verticalOffsetDesktop: "20",
    verticalOffsetMobile: "20",
    videoPlayFrequency: "20",
    mobileOrDesktopView: "Desktop",
  });

  const handleUpdateAppearance = React.useCallback(
    (field: keyof TAppearance, value: string) => {
      setAppearance((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const [codeSnippet, setCodeSnippet] = React.useState<string>("");
  const [isOnboardingVisible, setIsOnboardingVisible] =
    React.useState<boolean>(false);
  const [currentOnboardingStepIndex, setCurrentOnboardingStepIndex] =
    React.useState<number>(0);
  const hasInitializedOnboardingRef = React.useRef(false);
  const hasTrackedOnboardingStartRef = React.useRef(false);
  const viewedOnboardingStepsRef = React.useRef<Set<string>>(new Set());
  const onboardingStorageKey =
    session?.clinicId ?? session?.clinicSdkKeys?.[0] ?? null;

  const getOnboardingStartStepForTab = React.useCallback((tab: TTabName) => {
    if (!(tab in FIRST_DEPLOY_WIDGET_ONBOARDING_STEP_BY_TAB)) {
      return 0;
    }

    return FIRST_DEPLOY_WIDGET_ONBOARDING_STEP_BY_TAB[
      tab as TDeployWidgetOnboardingTab
    ];
  }, []);

  const markTabVisited = React.useCallback((tab: TTabName) => {
    setVisitedTabs((prev) => {
      if (prev.has(tab)) return prev;
      const next = new Set(prev);
      next.add(tab);
      return next;
    });
  }, []);

  const syncOnboardingStepToTab = React.useCallback(
    (tab: TTabName) => {
      if (!isOnboardingVisible) return;
      if (!(tab in FIRST_DEPLOY_WIDGET_ONBOARDING_STEP_BY_TAB)) return;

      const nextStepIndex =
        FIRST_DEPLOY_WIDGET_ONBOARDING_STEP_BY_TAB[
          tab as keyof typeof FIRST_DEPLOY_WIDGET_ONBOARDING_STEP_BY_TAB
        ];

      setCurrentOnboardingStepIndex(nextStepIndex);
    },
    [isOnboardingVisible],
  );

  const goToTab = React.useCallback(
    (tab: TTabName, options?: { syncOnboardingStep?: boolean }) => {
      setSelectedTab(tab);
      markTabVisited(tab);

      if (options?.syncOnboardingStep) {
        syncOnboardingStepToTab(tab);
      }
    },
    [markTabVisited, syncOnboardingStepToTab],
  );

  const handleSelectWorkflow = React.useCallback(
    (workflow: LeadConversionWorkflow) => {
      setSelectedWorkflow(workflow);
      setIsOnboardingVisible(false);

      if (workflow === "social") {
        setFormFormat((prev) => ({ ...prev, formName: "onboarding-tier1" }));
        setSelectedTab("Appearance");
        setVisitedTabs(new Set<TTabName>(["Appearance"]));
      } else {
        setSelectedTab("Profile");
        setVisitedTabs(new Set<TTabName>(["Profile"]));
      }

      mixpanel.track("Deploy Widget Workflow Selected", {
        clinicId: session?.clinicId,
        "Clinic Name": session?.clinicName,
        workflow,
      });
      mixpanel.track("Lead Conversion Channel Selected", {
        clinicId: session?.clinicId,
        "Clinic Name": session?.clinicName,
        "Lead Conversion Channel":
          LEAD_CONVERSION_CHANNEL_LABEL_BY_WORKFLOW[workflow],
      });
    },
    [session?.clinicId, session?.clinicName],
  );

  const handleChangeWorkflow = React.useCallback(() => {
    setSelectedWorkflow(null);
    setIsOnboardingVisible(false);
  }, []);

  const openOnboarding = React.useCallback(
    (tab: TTabName) => {
      setCurrentOnboardingStepIndex(getOnboardingStartStepForTab(tab));
      setIsOnboardingVisible(true);
    },
    [getOnboardingStartStepForTab],
  );

  React.useEffect(() => {
    if (hasInitializedOnboardingRef.current) return;
    if (selectedWorkflow !== "website") return;
    if (!onboardingStorageKey) return;

    hasInitializedOnboardingRef.current = true;

    if (
      !isTourDrivenOnboarding &&
      hasSeenDeployWidgetOnboarding(onboardingStorageKey)
    ) {
      return;
    }

    openOnboarding("Profile");
  }, [
    hasSeenDeployWidgetOnboarding,
    isTourDrivenOnboarding,
    onboardingStorageKey,
    openOnboarding,
    selectedWorkflow,
  ]);

  React.useEffect(() => {
    if (!isOnboardingVisible || hasTrackedOnboardingStartRef.current) return;

    hasTrackedOnboardingStartRef.current = true;

    mixpanel.track("Deploy Widget Onboarding Started", {
      clinicId: session?.clinicId,
      "Clinic Name": session?.clinicName,
    });
  }, [isOnboardingVisible, session?.clinicId, session?.clinicName]);

  const currentOnboardingStep =
    DEPLOY_WIDGET_ONBOARDING_STEPS[currentOnboardingStepIndex];

  React.useEffect(() => {
    if (!isOnboardingVisible || !currentOnboardingStep) return;
    if (viewedOnboardingStepsRef.current.has(currentOnboardingStep.id)) return;

    viewedOnboardingStepsRef.current.add(currentOnboardingStep.id);

    mixpanel.track("Deploy Widget Onboarding Step Viewed", {
      clinicId: session?.clinicId,
      "Clinic Name": session?.clinicName,
      step: currentOnboardingStep.id,
      tab: currentOnboardingStep.tab,
      stepNumber: currentOnboardingStepIndex + 1,
      totalSteps: DEPLOY_WIDGET_ONBOARDING_STEPS.length,
    });
  }, [
    currentOnboardingStep,
    currentOnboardingStepIndex,
    isOnboardingVisible,
    session?.clinicId,
    session?.clinicName,
  ]);

  const handleDismissOnboarding = React.useCallback(() => {
    setIsOnboardingVisible(false);

    if (onboardingStorageKey) {
      markDeployWidgetOnboardingSeen(onboardingStorageKey);
    }

    markStepComplete(TOOLTIP_STEPS.DEPLOY_WIDGET_TIPS_COMPLETED);

    mixpanel.track("Deploy Widget Onboarding Dismissed", {
      clinicId: session?.clinicId,
      "Clinic Name": session?.clinicName,
      step: currentOnboardingStep?.id,
      tab: currentOnboardingStep?.tab,
      stepNumber: currentOnboardingStepIndex + 1,
    });
  }, [
    currentOnboardingStep?.id,
    currentOnboardingStep?.tab,
    currentOnboardingStepIndex,
    markDeployWidgetOnboardingSeen,
    markStepComplete,
    onboardingStorageKey,
    session?.clinicId,
    session?.clinicName,
  ]);

  const handleAdvanceOnboarding = React.useCallback(() => {
    const isLastStep =
      currentOnboardingStepIndex === DEPLOY_WIDGET_ONBOARDING_STEPS.length - 1;

    if (isLastStep) {
      setIsOnboardingVisible(false);

      if (onboardingStorageKey) {
        markDeployWidgetOnboardingSeen(onboardingStorageKey);
      }

      markStepComplete(TOOLTIP_STEPS.DEPLOY_WIDGET_TIPS_COMPLETED);

      mixpanel.track("Deploy Widget Onboarding Completed", {
        clinicId: session?.clinicId,
        "Clinic Name": session?.clinicName,
        totalSteps: DEPLOY_WIDGET_ONBOARDING_STEPS.length,
      });
      return;
    }

    const nextStepIndex = currentOnboardingStepIndex + 1;
    const nextStep = DEPLOY_WIDGET_ONBOARDING_STEPS[nextStepIndex];

    setCurrentOnboardingStepIndex(nextStepIndex);

    if (selectedTab !== nextStep.tab) {
      goToTab(nextStep.tab, { syncOnboardingStep: false });
    }
  }, [
    currentOnboardingStepIndex,
    goToTab,
    markDeployWidgetOnboardingSeen,
    markStepComplete,
    onboardingStorageKey,
    selectedTab,
    session?.clinicId,
    session?.clinicName,
  ]);

  const highlightedStepId =
    isOnboardingVisible && currentOnboardingStep
      ? currentOnboardingStep.id
      : undefined;

  const fetchWidgetSettings = React.useCallback(async () => {
    if (hasFetchedWidgetSettingsRef.current) return;
    setIsFetchingWidgetSettings(true);
    setAreWidgetSettingsReady(false);
    try {
      const widgetSettingsUrl = `https://www.clinic-assets.clinic-os.com/${session?.clinicSdkKeys[0]}/widget_settings.json`;
      const response = await fetch(widgetSettingsUrl, {
        cache: "no-store",
      });
      const data = await response.json();

      // Filter out 'isDesktopView' from the settings
      const filteredData = Object.fromEntries(
        Object.entries(data).filter(([key]) => key !== "mobileOrDesktopView"),
      );

      const stringifiedData = Object.fromEntries(
        Object.entries(filteredData).map(([key, value]) => [
          key,
          String(value),
        ]),
      );

      setAppearance((prev) => ({
        ...prev,
        ...stringifiedData,
        launcherStyle:
          stringifiedData.launcherStyle === "minimalButton"
            ? "minimalButton"
            : "classic",
        launcherText:
          stringifiedData.launcherText?.trim() || DEFAULT_LAUNCHER_TEXT,
        textContent:
          stringifiedData.textContent?.trim() || DEFAULT_WIDGET_TEXT_CONTENT,
      }));
      hasFetchedWidgetSettingsRef.current = true;
    } catch (error) {
      console.error("Error fetching widget settings", error);
    } finally {
      setIsFetchingWidgetSettings(false);
      setAreWidgetSettingsReady(true);
    }
  }, [session?.clinicSdkKeys]);

  React.useEffect(() => {
    if (selectedWorkflow === "social" || selectedTab === "Appearance") {
      void fetchWidgetSettings();
    }
  }, [fetchWidgetSettings, selectedTab, selectedWorkflow]);

  const standaloneFormUrl = React.useMemo(() => {
    if (!session?.clinicSdkKeys[0]) return "";
    return buildFormPreviewUrl(
      session.clinicSdkKeys[0],
      appearance,
      formFormat,
    );
  }, [appearance, formFormat, session?.clinicSdkKeys]);

  React.useEffect(() => {
    if (isAdminMode) return;
    if (
      formFormat.formName !== "onboarding-tier1" &&
      formFormat.formName !== "onboarding-lite"
    ) {
      setFormFormat((prev) => ({ ...prev, formName: "onboarding-tier1" }));
    }
  }, [formFormat.formName, isAdminMode]);

  React.useEffect(() => {
    if (formFormat.formName === "onboarding-lite") {
      setCodeSnippet(buildIframeSnippet(standaloneFormUrl));
      return;
    }

    setCodeSnippet(`  <script
    src="https://www.clinic-os.com/clinicos-full-form-${process.env.EXPO_PUBLIC_ENV === "production" ? "prod" : "dev"}-v2.js"
    clinicSdkKey="${session?.clinicSdkKeys[0]}"
    formName="${formFormat.formName}"
  ></script>`);
  }, [formFormat.formName, session, standaloneFormUrl]);

  return (
    <View style={styles.screen}>
      <ScrollView
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollViewContent}
      >
        {!selectedWorkflow ? (
          <View style={styles.workflowChooser}>
            <TitleText
              text="Where do you want to convert leads?"
              style={styles.workflowTitle}
            />
            <View style={styles.workflowOptions}>
              {LEAD_CONVERSION_WORKFLOW_OPTIONS.map((option) => (
                <TouchableOpacity
                  key={option.id}
                  accessibilityRole="button"
                  accessibilityLabel={`Convert leads from ${option.title}`}
                  onPress={() => handleSelectWorkflow(option.id)}
                  style={styles.workflowOption}
                >
                  <View style={styles.workflowIconContainer}>
                    <MaterialIcons
                      name={option.iconName}
                      size={24}
                      color={ADMIN_TOKENS.color.accentDark}
                    />
                  </View>
                  <NormalText
                    text={option.title}
                    fontWeight="700"
                    style={styles.workflowOptionTitle}
                  />
                  <NormalText
                    text={option.description}
                    style={styles.workflowOptionDescription}
                  />
                </TouchableOpacity>
              ))}
            </View>
          </View>
        ) : (
          <>
            <View style={styles.headerRow}>
              <TitleText
                text={
                  selectedWorkflow === "social"
                    ? "Share Lead Capture Link"
                    : "Deploy Lead Conversion Form"
                }
                style={{ marginTop: 5 }}
              />
              <View style={styles.headerActions}>
                <TouchableOpacity
                  accessibilityRole="button"
                  accessibilityLabel="Change lead conversion workflow"
                  onPress={handleChangeWorkflow}
                  style={styles.headerActionButton}
                >
                  <MaterialIcons
                    name="swap-horiz"
                    size={24}
                    color={ADMIN_TOKENS.color.accent}
                  />
                  <NormalText text="Change workflow" fontWeight="500" />
                </TouchableOpacity>
                {selectedWorkflow === "website" ? (
                  <TouchableOpacity
                    accessibilityRole="button"
                    accessibilityLabel={
                      isOnboardingVisible
                        ? "Hide deploy widget onboarding tips"
                        : "Show deploy widget onboarding tips"
                    }
                    onPress={() => {
                      if (isOnboardingVisible) {
                        handleDismissOnboarding();
                        return;
                      }

                      openOnboarding(selectedTab);
                    }}
                    style={styles.headerActionButton}
                  >
                    <MaterialIcons
                      name="info-outline"
                      size={24}
                      color={ADMIN_TOKENS.color.accent}
                    />
                    <NormalText
                      text={isOnboardingVisible ? "Hide tips" : "Show tips"}
                      fontWeight="500"
                    />
                  </TouchableOpacity>
                ) : null}
              </View>
            </View>
            {selectedWorkflow === "social" ? (
              <View style={styles.socialWorkflow}>
                <View style={styles.singleTabContainer}>
                  <View style={styles.singleSelectedTab}>
                    <NormalText
                      text="Appearance"
                      fontWeight="600"
                      style={styles.singleTabText}
                    />
                  </View>
                  <View style={styles.singleTabLine} />
                </View>
                <AppearanceTab
                  appearance={appearance}
                  shouldRenderPreview={true}
                  onContinue={() => undefined}
                  onBack={handleChangeWorkflow}
                  onUpdateAppearance={handleUpdateAppearance}
                  isFetchingWidgetSettings={isFetchingWidgetSettings}
                  areWidgetSettingsReady={areWidgetSettingsReady}
                  continueLabel="Continue"
                  hideBackButton={true}
                  previewType="form"
                  formPreviewUrl={standaloneFormUrl}
                />
                <LeadCaptureShareLinkSection
                  clinicSdkKey={session?.clinicSdkKeys[0]}
                  deployMode="widget"
                  formName="onboarding-tier1"
                  session={session}
                />
              </View>
            ) : (
              <Tab
                tabNames={["Profile", "Appearance", "Deploy"]}
                currentTab={selectedTab}
                onTabPress={(tabKey) =>
                  goToTab(tabKey as TTabName, { syncOnboardingStep: true })
                }
                tabContent={[
                  <ProfileTab
                    key="Profile"
                    clinicSdkKey={session?.clinicSdkKeys[0]}
                    profile={profile}
                    setProfile={setProfile}
                    appearance={appearance}
                    formFormat={formFormat}
                    shouldRenderPreview={visitedTabs.has("Profile")}
                    showAllFormTypes={isAdminMode}
                    highlightedStepId={highlightedStepId}
                    onUpdateFormFormat={(field, value) =>
                      setFormFormat((prev) => ({ ...prev, [field]: value }))
                    }
                    onContinue={() =>
                      goToTab("Appearance", { syncOnboardingStep: true })
                    }
                  />,
                  <AppearanceTab
                    key="Appearance"
                    appearance={appearance}
                    shouldRenderPreview={visitedTabs.has("Appearance")}
                    highlightedStepId={highlightedStepId}
                    onContinue={() =>
                      goToTab("Deploy", { syncOnboardingStep: true })
                    }
                    onBack={() =>
                      goToTab("Profile", { syncOnboardingStep: true })
                    }
                    onUpdateAppearance={handleUpdateAppearance}
                    isFetchingWidgetSettings={isFetchingWidgetSettings}
                    areWidgetSettingsReady={areWidgetSettingsReady}
                  />,
                  <DeployTab
                    key="Deploy"
                    codeSnippet={codeSnippet}
                    deployMode={
                      formFormat.formName === "onboarding-lite"
                        ? "iframe"
                        : "widget"
                    }
                    deployStatus={session?.deployStatus}
                    standaloneFormUrl={standaloneFormUrl}
                    focusSectionId={
                      currentOnboardingStep?.tab === "Deploy"
                        ? "custom-html"
                        : undefined
                    }
                    highlightedStepId={highlightedStepId}
                    onBack={() =>
                      goToTab("Appearance", { syncOnboardingStep: true })
                    }
                    hideBackButton={true}
                  />,
                ]}
              />
            )}
          </>
        )}
      </ScrollView>
      {selectedWorkflow === "website" &&
      isOnboardingVisible &&
      currentOnboardingStep ? (
        <View pointerEvents="box-none" style={styles.onboardingOverlay}>
          <DeployWidgetOnboardingCard
            step={currentOnboardingStep}
            stepIndex={currentOnboardingStepIndex}
            totalSteps={DEPLOY_WIDGET_ONBOARDING_STEPS.length}
            onNext={handleAdvanceOnboarding}
            onDismiss={handleDismissOnboarding}
          />
        </View>
      ) : null}
      {selectedWorkflow === "website" &&
      selectedTab === "Deploy" &&
      !isOnboardingVisible ? (
        <DeployHelpWidget onShowTips={() => openOnboarding("Deploy")} />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    position: "relative",
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: ADMIN_TOKENS.spacing.md,
    flexWrap: "wrap",
  },
  headerActions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: ADMIN_TOKENS.spacing.sm,
  },
  headerActionButton: {
    flexDirection: "row",
    gap: ADMIN_TOKENS.spacing.xs,
    paddingVertical: ADMIN_TOKENS.spacing.sm,
    paddingHorizontal: ADMIN_TOKENS.spacing.md,
    minHeight: ADMIN_TOKENS.size.inputHeight,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: ADMIN_TOKENS.radius.lg,
    borderWidth: ADMIN_TOKENS.borderWidth.sm,
    borderColor: ADMIN_TOKENS.color.borderAccent,
    backgroundColor: ADMIN_TOKENS.color.surface,
  },
  scrollViewContent: {
    flexGrow: 1,
    paddingBottom: 220,
  },
  workflowChooser: {
    width: "100%",
    maxWidth: ADMIN_TOKENS.size.contentMaxWidth,
    gap: ADMIN_TOKENS.spacing.xxl,
    paddingTop: ADMIN_TOKENS.spacing.huge,
  },
  workflowTitle: {
    marginTop: ADMIN_TOKENS.spacing.xs,
  },
  workflowOptions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: ADMIN_TOKENS.spacing.lg,
  },
  workflowOption: {
    flexGrow: 1,
    flexBasis: 260,
    maxWidth: 360,
    minHeight: 180,
    gap: ADMIN_TOKENS.spacing.md,
    padding: ADMIN_TOKENS.spacing.xxl,
    borderWidth: ADMIN_TOKENS.borderWidth.sm,
    borderColor: ADMIN_TOKENS.color.border,
    borderRadius: ADMIN_TOKENS.radius.xl,
    backgroundColor: ADMIN_TOKENS.color.surface,
    ...ADMIN_TOKENS.shadow.card,
  },
  workflowIconContainer: {
    width: ADMIN_TOKENS.spacing.massive,
    height: ADMIN_TOKENS.spacing.massive,
    borderRadius: ADMIN_TOKENS.radius.xxl,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: ADMIN_TOKENS.color.surfaceAccent,
  },
  workflowOptionTitle: {
    fontSize: ADMIN_TOKENS.fontSize.xl,
    lineHeight: ADMIN_TOKENS.lineHeight.xl,
    color: ADMIN_TOKENS.color.textPrimary,
  },
  workflowOptionDescription: {
    fontSize: ADMIN_TOKENS.fontSize.md,
    lineHeight: ADMIN_TOKENS.lineHeight.md,
    color: ADMIN_TOKENS.color.textSecondary,
  },
  socialWorkflow: {
    gap: ADMIN_TOKENS.spacing.xxl,
  },
  singleTabContainer: {
    flexDirection: "row",
    width: "100%",
    position: "relative",
    marginBottom: ADMIN_TOKENS.spacing.md,
  },
  singleSelectedTab: {
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: ADMIN_TOKENS.spacing.md,
    borderBottomColor: ADMIN_TOKENS.color.textPrimary,
    borderBottomWidth: ADMIN_TOKENS.borderWidth.sm,
    zIndex: ADMIN_TOKENS.zIndex.above,
  },
  singleTabText: {
    fontSize: ADMIN_TOKENS.fontSize.xl,
    lineHeight: ADMIN_TOKENS.lineHeight.xl,
    color: ADMIN_TOKENS.color.textPrimary,
  },
  singleTabLine: {
    bottom: 0,
    left: 0,
    width: "100%",
    borderBottomWidth: ADMIN_TOKENS.borderWidth.sm,
    flexDirection: "row",
    borderBottomColor: ADMIN_TOKENS.color.borderStrong,
    position: "absolute",
  },
  onboardingOverlay: {
    position: "absolute",
    right: 16,
    bottom: 24,
    left: 16,
    alignItems: "flex-end",
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
});

export default GetStarted;
